import { addYears } from "date-fns";
import { humanReadableBytes } from "@sscs/utils";
import * as yup from "Utils/yup";
import { ExportFormData, TimeRangeFormData } from "./types";

export const TimeRangeFormSchema: yup.SchemaOf<TimeRangeFormData> = yup.object({
  startDate: yup
    .string()
    .required()
    .test(
      "max-year-past",
      "Cannot filter older than one year",
      (value: string) => {
        if (!value) return true;

        const yearAgo = addYears(new Date(), -1);
        return new Date(value) >= yearAgo;
      },
    ),
  endDate: yup
    .string()
    .required()
    .test(
      "is-after",
      '"To" must be later than "From"',
      (value: string, testContext: yup.TestContext) => {
        const startDate = testContext.parent.startDate;

        if (!startDate || !value) return true;

        return new Date(value) > new Date(startDate);
      },
    )
    .test(
      "is-thirty-days",
      "Maximum range is 30 days",
      (value: string, testContext: yup.TestContext) => {
        const startDate = testContext.parent.startDate;

        if (!startDate || !value) return true;

        const dayMilliseconds = 1000 * 60 * 60 * 24;
        const diffDays =
          (new Date(value).getTime() - new Date(startDate).getTime()) /
          dayMilliseconds;
        return diffDays >= 0 && diffDays <= 30;
      },
    ),
});

export const AuditExportSchema: yup.SchemaOf<Omit<ExportFormData, "filters">> =
  yup.object({
    name: yup
      .string()
      .required("Name is required")
      .purlSafe()
      .noInvisibleString("Name should not be blank")
      .max(255, "Name must be at most 255 characters"),
    should_create_cef: yup.boolean().default(false),
    should_create_ndjson: yup
      .boolean()
      .default(false)
      .when("should_create_cef", {
        is: false,
        then: (schema) =>
          schema.test(
            "exportTypeSelected",
            "At least one export type must be selected",
            function (value: boolean) {
              return value;
            },
          ),
      }),
  });

export const transformValue = (
  key: string,
  value: string | null,
): string | null => {
  if (!key.endsWith("_bytes") || !value || isNaN(parseInt(value, 10)))
    return value;

  return humanReadableBytes(parseInt(value, 10));
};
